import { useContext, useEffect, useRef } from 'react';
import { useSpring } from '@react-spring/core';
import { TRect } from '@src/types/misc';
import {
  calculateLocations,
  TOverlayLocations
} from '@src/utils/wysiwyg_editor/contentItemHandleLocations';
import useLocationInViewbox from '../wysiwyg_editor/useLocationInViewbox';
import useDomLocationInWysiwygContainer from './useDomLocationInWysiwygContainer';
import {
  ContentSpringLocations,
  PageContentStateContext
} from '@src/contexts/wysiwyg/page_content';
import useNoRenderRef from '../useNoRenderRef';

interface LocatableItem {
  containerLocation: TRect;
  contentLocation: TRect;
}

function useContentItemSprings(data: LocatableItem): ContentSpringLocations {
  const { lastHistoryWalk } = useContext(PageContentStateContext);
  const clipLocationInDom = useDomLocationInWysiwygContainer(
    data.containerLocation
  );
  const clipLocationInViewbox = useLocationInViewbox(data.containerLocation);

  const [frameViewboxSpringValues, frameViewboxSpringApi] = useSpring<TRect>(
    () => ({
      config: { duration: 1 },
      ...clipLocationInViewbox
    }),
    // make sure to keep lastHistoryWalk so if we undo/redo the springs get
    // updated
    [clipLocationInViewbox, lastHistoryWalk]
  );

  const [
    frameDomSpringValues,
    frameDomSpringApi
  ] = useSpring<TOverlayLocations>(
    () => ({
      config: { duration: 1 },
      ...calculateLocations(clipLocationInDom)
    }),
    [clipLocationInDom]
  );

  const noRenderFrameDomSpringApi = useNoRenderRef(frameDomSpringApi);
  const noRenderFrameDomSpringValues = useNoRenderRef(frameDomSpringValues);
  const noRenderFrameViewboxSpringApi = useNoRenderRef(frameViewboxSpringApi);
  const noRenderFrameViewboxSpringValues = useNoRenderRef(
    frameViewboxSpringValues
  );

  const springsRef = useRef<ContentSpringLocations>({
    frameDomSpringApi: noRenderFrameDomSpringApi.current,
    frameDomSpringValues: noRenderFrameDomSpringValues.current,
    frameViewboxSpringApi: noRenderFrameViewboxSpringApi.current,
    frameViewboxSpringValues: noRenderFrameViewboxSpringValues.current
  });

  useEffect(() => {
    const newSprings = {
      frameDomSpringApi: noRenderFrameDomSpringApi.current,
      frameDomSpringValues: noRenderFrameDomSpringValues.current,
      frameViewboxSpringApi: noRenderFrameViewboxSpringApi.current,
      frameViewboxSpringValues: noRenderFrameViewboxSpringValues.current
    };

    springsRef.current = newSprings;
  }, [
    // make sure lastHistoryWalk is included so that undo/redo will cause a
    // proper re-render
    lastHistoryWalk,
    noRenderFrameDomSpringApi,
    noRenderFrameDomSpringValues,
    noRenderFrameViewboxSpringApi,
    noRenderFrameViewboxSpringValues
  ]);

  return springsRef.current;
}

export default useContentItemSprings;
